import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react'

const MIN_DRAWER_WIDTH = 360
// Wide enough for a table page (the audit log) to show every column without a scroll bar.
// clampWidth also keeps the drawer inside the viewport.
const MAX_DRAWER_WIDTH = 1200
const STORAGE_KEY = 'nobgp:drawerWidth'

function clampWidth(value: number, margin: number) {
  const viewportMax = Math.max(MIN_DRAWER_WIDTH, window.innerWidth - margin * 2)
  return Math.min(Math.max(value, MIN_DRAWER_WIDTH), Math.min(MAX_DRAWER_WIDTH, viewportMax))
}

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return MIN_DRAWER_WIDTH
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : MIN_DRAWER_WIDTH
  } catch {
    return MIN_DRAWER_WIDTH
  }
}

interface UseDrawerResizeOptions {
  enabled: boolean
  margin: number
}

export default function useDrawerResize({ enabled, margin }: UseDrawerResizeOptions) {
  const [width, setWidth] = useState<number>(() => clampWidth(readStoredWidth(), margin))
  const [isResizing, setIsResizing] = useState(false)
  const widthRef = useRef(width)
  widthRef.current = width

  // Re-clamp width when the viewport or margin changes so the drawer never overflows
  useEffect(() => {
    const handleResize = () => {
      setWidth(w => {
        const next = clampWidth(w, margin)
        return next === w ? w : next
      })
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [margin])

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled) return
      event.preventDefault()
      event.stopPropagation()

      const startX = event.clientX
      const startWidth = widthRef.current
      let latestWidth = startWidth
      const target = event.currentTarget
      try {
        target.setPointerCapture(event.pointerId)
      } catch {
        // setPointerCapture can throw in some edge cases — safe to ignore
      }
      setIsResizing(true)

      const handleMove = (e: PointerEvent) => {
        const next = clampWidth(startWidth + (e.clientX - startX), margin)
        if (next !== latestWidth) {
          latestWidth = next
          setWidth(next)
        }
      }

      const handleUp = () => {
        setIsResizing(false)
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
        window.removeEventListener('pointercancel', handleUp)
        try {
          target.releasePointerCapture(event.pointerId)
        } catch {
          // releasePointerCapture can throw if the pointer was never captured — safe to ignore
        }
        try {
          localStorage.setItem(STORAGE_KEY, String(latestWidth))
        } catch {
          // storage may be unavailable (e.g. private mode) — ignore
        }
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
      window.addEventListener('pointercancel', handleUp)
    },
    [enabled, margin]
  )

  const effectiveWidth = enabled ? width : MIN_DRAWER_WIDTH

  return {
    width: effectiveWidth,
    isResizing,
    handleProps: { onPointerDown: handlePointerDown },
  }
}
